import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import { NO_DATA_AVAILABLE } from '../../../constants/common'
import {Flex, Form, Modal, Row, Space} from 'antd'
import {DownloadOutlined, ExclamationCircleOutlined} from '@ant-design/icons'
import {useIntl} from 'react-intl'
import {deleteRequest, getRequest} from '../../../services/base'
import {NavContext} from '../../../context/global_context'
import useDebouncedValue from '../../../utils/common_function'
import {buildPageUrl, downloadFileBlob, getDeleteTargetPage} from '../../../utils/common'
import InputText from '../../common/InputText'
import Button from '../../common/Button'
import {SELECT_SHIFT, SET_SHIFT_LIST, ShiftContext} from './context/context'
import ShiftTable from './ShiftTable'
import ShiftFormModal from './form'
import { notifyError } from '../../../utils/notifications'

const Shifts = () => {
  const intl = useIntl()
  const [state, dispatch] = useContext(ShiftContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const currentUrl = useRef('/api/shift/')

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const getShiftListData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'shifts.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_SHIFT_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'shifts.loadError' }))
    }
  }

  useEffect(() => {
    getShiftListData()
  }, [])

  useEffect(() => {
    const url = `/api/shift/?search=${search}`
    currentUrl.current = url
    getShiftListData()
  }, [debouncedSearch])

  const deleteShift = async (record) => {
    let res = await deleteRequest(`/api/shift/${record.id}/`)
    if (res?.status) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'shifts.deleteSuccess' }),
        },
      })
      currentUrl.current = buildPageUrl(currentUrl.current, getDeleteTargetPage(pagination))
      getShiftListData()
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'shifts.deleteError' }),
        },
      })
    }
  }

  const confirmDelete = (record) => {
    modal.confirm({
      title: intl.formatMessage({ id: 'shifts.confirmDelete' }),
      icon: <ExclamationCircleOutlined />,
      content: intl.formatMessage(
        { id: 'shifts.confirmDeleteContent' },
        { name: record.name }
      ),
      onOk: () => deleteShift(record),
      centered: true,
      maskClosable: true,
    })
  }

  const downloadShifts = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'shifts.downloadCaption' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await getRequest(`/api/shift/download/`, true, true)
    navDispatch({
      type: 'loader',
      data: { showLoader: false },
      notification: {},
    })
    // `res?.msg` was redundant here: base.js sets status false on every failure,
    // and an error body is never a Blob.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type:
            res?.code === NO_DATA_AVAILABLE ? 'info' : 'error',
          msg: intl.formatMessage({
            id:
              res?.code === NO_DATA_AVAILABLE
                ? 'generic.noDataToExport'
                : 'shifts.downloadError',
          }),
        },
      })
    } else {
      downloadFileBlob(res, 'shifts.csv')
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'shifts.downloadCompleted' }),
        },
      })
    }
  }

  const onCreateShift = () => {
    dispatch({
      type: SELECT_SHIFT,
      data: { selected_shift: {}, show_shift_popup: true },
    })
  }

  const onEditShift = (record) => {
    dispatch({
      type: SELECT_SHIFT,
      data: { selected_shift: record, show_shift_popup: true },
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.shift_list?.length) return []
    return state.shift_list.map((item, index) => ({
      ...item,
      key: `shift-${index}`,
      rowKey: `shift-${item.id}`,
      index,
    }))
  }, [state.shift_list])

  return (
    <>
      {contextHolder}

      {state.show_shift_popup && (
        <ShiftFormModal getShiftListData={getShiftListData} />
      )}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width administration-form">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_shifts"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'shifts.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                className="full-width"
                wrapperClass="search-shifts"
                onChange={(value) => setSearch(value)}
              />
              <Space size="middle" align="center">
                <Button onClick={downloadShifts} icon={<DownloadOutlined />}>
                  {intl.formatMessage({ id: 'shifts.export' })}
                </Button>
                <Button onClick={onCreateShift}>
                  {intl.formatMessage({ id: 'shifts.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <ShiftTable
              dataSource={dataSource}
              pagination={pagination}
              getShiftListData={getShiftListData}
              onEdit={onEditShift}
              onDelete={confirmDelete}
            />
          </Flex>
        </Row>
      </div>
    </>
  )
}

export default Shifts
